import { useEffect, useEffectEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route, Routes as RoutesRouter } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';

import {
  INSPECTION_STATUSES,
  PAYMENT_STATUSES,
  Profile,
  useReportContext,
  useSessionMode,
  useVehicleLookup,
  useWebSockets,
} from '@motorway/motorway-handshake-components';

import { BackButton } from '../../components/BackButton/BackButton';
import { useDriver } from '../../contexts/Driver';
import { ROUTES } from '../../helpers/routes';
import { snowplowEvent } from '../../helpers/snowplow';
import { Cancelled } from '../../pages/Cancelled/Cancelled';
import { Complete } from '../../pages/Complete/Complete';
import { DamageReport } from '../../pages/DamageReport/DamageReport';
import { End } from '../../pages/End/End';
import { Finished } from '../../pages/Finished/Finished';
import { Home } from '../../pages/Home/Home';
import { Inspection } from '../../pages/Inspection/Inspection';
import { OTPCode } from '../../pages/OTPCode/OTPCode';
import { Outcome } from '../../pages/Outcome/Outcome';
import { Pending } from '../../pages/Pending/Pending';
import { SendReport } from '../../pages/SendReport/SendReport';
import { Adjustment } from '../Adjustment/Adjustment';

export const Routes = () => {
  const { driver } = useDriver();
  const { report } = useReportContext();
  const { setSessionMode } = useSessionMode();
  const { status } = useWebSockets();
  const { vehicle } = useVehicleLookup();

  const { code, token } = driver ?? {};
  const { connected, paymentStatus, sessionMode, status: inspectionStatus } = status;
  const { enquiry } = vehicle ?? {};

  // https://github.com/remix-run/react-router/issues/7634
  // `navigate` is not "useCallback"ed so a re-render will and does cause the useEffect to fire again
  // This usually does not matter, an extra render, but since we're navigating, we re-navigate every store update
  // and not just when the status changes
  const navigate = useNavigate();
  const navigateEvent: NavigateFunction = useEffectEvent(navigate);

  useEffect(() => {
    if (sessionMode) {
      setSessionMode(sessionMode);
    }
  }, [setSessionMode, sessionMode]);

  useEffect(() => {
    if (!connected || !inspectionStatus) {
      return;
    }

    const isLite = sessionMode === 'lite';
    // Non-pay Lite (the majority) has no paymentStatus and stays on COMPLETE, not the paid screen.
    const isPaid = paymentStatus === PAYMENT_STATUSES.PAYMENT_COMPLETE;

    const PATHS = [
      [INSPECTION_STATUSES.INITIATED, ROUTES.INSPECTION],
      [INSPECTION_STATUSES.CONFIRMATION_PENDING, ROUTES.PENDING],
      // inspection_completed is emitted for Lite only — no mode/payment gate needed.
      [INSPECTION_STATUSES.COMPLETED, ROUTES.END],
      [INSPECTION_STATUSES.CONFIRMED, isLite && isPaid ? ROUTES.END : ROUTES.COMPLETE],
    ];

    const path = PATHS.find(([status]) => status === inspectionStatus)?.[1];

    if (path) {
      navigateEvent(path, { replace: true });
    }
  }, [connected, inspectionStatus, paymentStatus, sessionMode]); // `navigate` is left deliberately out

  useEffect(() => {
    // Analytics only — keyed on the status itself so payment/mode updates don't double-count.
    if (!connected || !inspectionStatus) {
      return;
    }

    snowplowEvent({
      label: inspectionStatus,
      name: 'inspection_status_changed',
      schema: 'load',
    });
  }, [connected, inspectionStatus]);

  const isLoading = [
    !enquiry?.id,
    !inspectionStatus,
    !report?.inspection,
    !token,
  ].some(Boolean);

  // `code` will be be replaced by `token` once the driver is signed in,
  // but we want to show the loading screen if neither are present
  if (!code) {
    if (isLoading) {
      return <Home loading />;
    }
  }

  return (
    <RoutesRouter>
      <Route path={ROUTES.CANCELLATION} element={<Adjustment />} />
      <Route path={ROUTES.CANCELLED} element={<Cancelled />} />
      <Route path={ROUTES.COMPLETE} element={<Complete />} />
      <Route path={ROUTES.DAMAGE_REPORT} element={<DamageReport />} />
      <Route path={ROUTES.END} element={<End />} />
      <Route path={ROUTES.FINISHED} element={<Finished />} />
      <Route path={ROUTES.INSPECTION} element={<Inspection />} />
      <Route path={ROUTES.OTP_CODE} element={<OTPCode />} />
      <Route path={ROUTES.OUTCOME} element={<Outcome />} />
      <Route path={ROUTES.PENDING} element={<Pending />} />
      <Route path={ROUTES.PRICE_ADJUSTMENT} element={<Adjustment />} />
      <Route path={ROUTES.PROFILE} element={<Profile backButton={(<BackButton padding />)} showHpiHistory />} />
      <Route path={ROUTES.SEND_REPORT} element={<SendReport />} />
      <Route path={ROUTES.HOME} element={<Home />} />
    </RoutesRouter>
  );
};
